import { AdSlotContainerRenderer } from './ad-slot-container.renderer';
import { StylesheetRenderer } from './stylesheet.renderer';
import { AdSlot } from '../../entities/ad-slot';
import { RendererInterface } from '../../interfaces/components/renderer/renderer.interface';
import { BrowserObjectModel } from '../browser-object-model';
import { Injectable, Optional } from '../dependency-injection/decorators';
import { AdSlotLifecycleEvent } from '../event-manager/event/ad-slot-lifecycle.event';
import { Events } from '../event-manager/event/events';
import { EventManager } from '../event-manager/event-manager';
import { Logger } from '../logger/logger';
import { AdSlotContainerResolver } from '../resolver/ad-slot-container.resolver';
import { AdSlotRenderValidator } from '../validator/ad-slot-render.validator';

@Injectable()
export class AdSlotRenderer implements RendererInterface {
  private mutationObservers: Map<string, MutationObserver> = new Map();
  private observedElements = new Set<Element>();

  public constructor (
    private readonly eventManager: EventManager,
    private readonly adSlotContainerResolver: AdSlotContainerResolver,
    private readonly adSlotRenderValidator: AdSlotRenderValidator,
    private readonly adSlotContainerRenderer: AdSlotContainerRenderer,
    private readonly stylesheetRenderer: StylesheetRenderer,
    private readonly browserObjectModel: BrowserObjectModel,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  async render (adSlot?: AdSlot): Promise<void> {
    if (!adSlot) {
      return;
    }

    // when the adSet has multiple adSlots, if we disconnect the observer
    // only new instances of the last rendered adSlot will be observed
    // if (this.mutationObserver) {
    //   this.mutationObserver.disconnect();
    // }

    const event = await this.eventManager.dispatch(
      Events.AD_SLOT_PRE_RENDER,
      new AdSlotLifecycleEvent(adSlot),
    );

    if (!this.adSlotRenderValidator.validate(adSlot) || event.isPropagationStopped()) {
      return;
    }

    const datasetId = adSlot.getSettings().getDatasetId();

    if (!datasetId) {
      this.logger?.error('Dataset ID is not defined');
      return;
    }

    await this.stylesheetRenderer.render(adSlot);

    // render for existing elements
    const elements: Element[] = [];

    this.browserObjectModel.getDocument().querySelectorAll<HTMLElement>(
      `ins[data-${datasetId}="${adSlot.getId()}"]`,
    ).forEach((i) => elements.push(i));

    await this.renderContainers(adSlot, elements);

    await this.eventManager.dispatch(Events.AD_SLOT_POST_RENDER, new AdSlotLifecycleEvent(adSlot));

    if (this.mutationObservers.has(adSlot.getId())) {
      return;
    }

    const mutationObserver = new MutationObserver((mutations: MutationRecord[]): void => {
      mutations.forEach((mutation: MutationRecord): void => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(async (node: Node): Promise<void> => {
            if (!(node instanceof Element)) {
              return;
            }

            let newNode: Element | null = node;

            if (newNode.tagName.toLowerCase() !== 'ins') {
              // search for ins elements in the subtree
              newNode = newNode.querySelector(`ins[data-${datasetId}="${adSlot.getId()}"]`);
            }

            if (
              !newNode
              || newNode.tagName.toLowerCase() !== 'ins'
              || !newNode.hasAttribute(`data-${datasetId}`)
              || newNode.getAttribute(`data-${datasetId}`) !== adSlot.getId()
              || (newNode.hasAttribute('data-rendered') && newNode.getAttribute('data-rendered') === 'true')
              || (newNode.hasAttribute('data-rendering') && newNode.getAttribute('data-rendering') === 'true')
              || newNode.childElementCount > 0
              || this.observedElements.has(newNode)
            ) {
              this.logger?.debug('Skipping node', newNode);
              return;
            }

            this.observedElements.add(newNode);
            newNode.setAttribute('data-rendering', 'true');

            await this.renderContainers(adSlot, [ newNode ]);
          });
        }
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
    this.mutationObservers.set(adSlot.getId(), mutationObserver);
  }

  private async renderContainers (adSlot: AdSlot, elements: Element[]): Promise<void> {
    const adSlotContainers = await this.adSlotContainerResolver.resolve(adSlot, elements);

    for (const adSlotContainer of adSlotContainers) {
      adSlot.addContainer(adSlotContainer);
      await this.adSlotContainerRenderer.render(adSlotContainer);
    }
  }
}
